// mainPageTour.js - Version that accepts translation function as parameter
import { useCallback, useState, useMemo } from 'react';
import { useTour } from './TourProvider';

export const useMainPageTour = (translationFunction) => {
  const tourContext = useTour();
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  
  // Use passed translation function or fallback
  const t = translationFunction || ((key) => key);
  
  const startTour = tourContext?.startTour || (() => console.warn('startTour not available'));
  const forceShowTour = tourContext?.forceShowTour || null;
  
  const mainPageTourData = useMemo(() => ({
    name: 'mainPageTour',
    steps: [
      {
        title: t('Welcome to the Game!'),
        content: t('This is your main gaming hub where you can start new games and customize your experience.'),
        additionalInfo: t('You can access this tour anytime by clicking the tour button at the bottom left.'),
        selector: '[data-tour="main-container"]',
        positioning: { position: 'auto' }
      },
      {
        title: t('Select Your Game Type'),
        content: t('Click here to choose who you want to play against - Human players, AI opponents, or team games.'),
        additionalInfo: t('Each opponent type offers different challenges and time limits. Human players provide the most dynamic experience.'),
        selector: '[data-tour="select-opponent"]',
        positioning: { position: 'below', align: 'center' }
      },
      {
        title: t('Choose Outcome Rewards'),
        content: t('Select your risk level and potential coin rewards. Higher stakes mean bigger rewards but also bigger losses!'),
        additionalInfo: t('Win, draw, and loss outcomes show different coin amounts. Choose wisely based on your confidence level.'),
        selector: '[data-tour="select-level"]',
        positioning: { position: 'above', align: 'center' }
      },
      {
        title: t('Start Playing!'),
        content: t('Once you\'ve made your selections, hit this button to find an opponent and start your game.'),
        additionalInfo: t('The system will automatically match you with an appropriate opponent based on your selections.'),
        selector: '[data-tour="play-button"]',
        positioning: { position: 'above', align: 'center' }
      }
    ]
  }), [t]);

  const shouldShowTour = useCallback(() => {
    const tourName = 'mainPageTour';
    
    try {
      const tourPreferences = localStorage.getItem('tourPreferences');
      if (tourPreferences) {
        const preferences = JSON.parse(tourPreferences);
        const tourPref = preferences[tourName];
        if (tourPref && tourPref.dontShowAgain) {
          return false;
        }
      }
    } catch (error) {
      console.error('Error checking tour preferences:', error);
    }

    try {
      const closedTours = sessionStorage.getItem('closedTours');
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        if (closedToursArray.includes(tourName)) {
          return false;
        }
      }
    } catch (error) {
      console.error('Error checking session storage:', error);
    }

    const hasVisitedMainPage = localStorage.getItem('hasVisitedMainPage');
    const isNewUser = localStorage.getItem('isNewUser') === 'true';
    
    return !hasVisitedMainPage || isNewUser;
  }, []);

  const startMainPageTour = useCallback(() => {
    if (!hasAutoStarted && typeof startTour === 'function') {
      try {
        const success = startTour(mainPageTourData);
        if (success !== false) {
          setHasAutoStarted(true);
          localStorage.setItem('hasVisitedMainPage', 'true');
          if (localStorage.getItem('isNewUser') === 'true') {
            localStorage.setItem('isNewUser', 'false');
          }
        }
      } catch (error) {
        console.error('Error starting tour:', error);
      }
    }
  }, [startTour, hasAutoStarted, mainPageTourData]);

  const forceShowMainPageTour = useCallback(() => {
    try {
      const tourPreferences = localStorage.getItem('tourPreferences');
      if (tourPreferences) {
        const preferences = JSON.parse(tourPreferences);
        if (preferences['mainPageTour']?.dontShowAgain) {
          delete preferences['mainPageTour'];
          localStorage.setItem('tourPreferences', JSON.stringify(preferences));
        }
      }

      const closedTours = sessionStorage.getItem('closedTours');
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        const updatedArray = closedToursArray.filter(tourName => tourName !== 'mainPageTour');
        sessionStorage.setItem('closedTours', JSON.stringify(updatedArray));
      }

      if (forceShowTour && typeof forceShowTour === 'function') {
        const success = forceShowTour(mainPageTourData);
        if (success !== false) {
          setHasAutoStarted(true);
        }
      } else if (typeof startTour === 'function') {
        const success = startTour(mainPageTourData);
        if (success !== false) {
          setHasAutoStarted(true);
        }
      }
    } catch (error) {
      console.error('Error force showing tour:', error);
    }
  }, [forceShowTour, startTour, mainPageTourData]);

  const resetTour = useCallback(() => {
    setHasAutoStarted(false);
    try {
      const closedTours = sessionStorage.getItem('closedTours');
      if (closedTours) {
        const closedToursArray = JSON.parse(closedTours);
        const updatedArray = closedToursArray.filter(tourName => tourName !== 'mainPageTour');
        sessionStorage.setItem('closedTours', JSON.stringify(updatedArray));
      }
    } catch (error) {
      console.error('Error clearing session closure:', error);
    }
  }, []);

  return {
    startMainPageTour,
    forceShowMainPageTour,
    shouldShowTour,
    resetTour,
    hasAutoStarted
  };
};